import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Theme, useTheme } from '@mui/material';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { patchParticipant } from 'store/callScribe';
import RenameSpeaker, { IRenamedSpeaker } from 'components/CallScribe/RenameSpeaker';
import {
  CallScribeLiveTranscriptForCallSelector,
  CallScribeParticipantsForCallSelector,
} from 'store/callScribe/selectors';
import { getCallScribeTranscriptSegments } from 'services/callScribe';
import {
  CallScribeConsentResult,
  ICallScribeParticipant,
  ILiveTranscriptSegment,
} from 'services/callScribe/types';
import { participantLabel } from 'components/CallScribe/participantLabel';
import { findParticipant } from 'services/callScribe/participants';
import { toLiveSegment } from 'hooks/useLiveTranscriptBackfill';
import {
  compareLiveTranscriptSegments,
  liveTranscriptSegmentKey,
} from 'services/callScribe/transcriptOrder';
import { speakerTone } from 'components/CallScribe/callScribeTheme';
import useConversationStyles from 'components/CallScribe/conversationStyles';
import { slotForLine, speakerSlots } from 'components/CallScribe/speakerSlots';
import { buildGroups } from 'components/CallScribe/turnGrouping';

export interface IConversationSectionProps {
  callId: number | undefined;
  live: boolean;
  consentResult?: CallScribeConsentResult | null;
  variant?: 'panel' | 'stage';
  listening?: boolean;
  focusStartMs?: number | null;
  participants?: ICallScribeParticipant[];
  canRename?: boolean;
}

const MAX_RENDERED_SEGMENTS = 300;

const formatMs = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const ConversationSection: React.FC<IConversationSectionProps> = ({
  callId,
  live,
  consentResult,
  variant = 'panel',
  listening = false,
  focusStartMs,
  participants: participantsProp,
  canRename = false,
}) => {
  const classes = useConversationStyles();
  const theme = useTheme<Theme>();
  const dispatch = useAppDispatch();
  const onGround = variant === 'stage';
  const headingId = useId();
  const [fetched, setFetched] = useState<ILiveTranscriptSegment[] | undefined>(undefined);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [waitingLong, setWaitingLong] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  const [pinned, setPinned] = useState(true);
  const startedAtTopRef = useRef(false);
  const refetchedRef = useRef(false);
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  const storeParticipants = useAppSelector(CallScribeParticipantsForCallSelector(callId));
  const participants = storeParticipants ?? participantsProp;

  const liveState = useAppSelector(
    CallScribeLiveTranscriptForCallSelector(live ? callId : undefined),
  );

  useEffect(() => {
    if (typeof callId !== 'number') {
      setFetched(undefined);
      setFetchFailed(false);
      return undefined;
    }
    let cancelled = false;
    setFetched(undefined);
    setFetchFailed(false);
    void getCallScribeTranscriptSegments(callId)
      .then(res => {
        if (cancelled) return;
        if (!res) {
          setFetchFailed(true);
          return;
        }
        setFetched(
          res.data.map(toLiveSegment),
        );
      })
      .catch(() => {
        if (!cancelled) setFetchFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  const committed = useMemo(() => {
    const byKey = new Map<string, ILiveTranscriptSegment>();
    (fetched ?? []).forEach(s => byKey.set(liveTranscriptSegmentKey(s), s));
    if (liveState) {
      Object.values(liveState.committed).forEach(s => byKey.set(liveTranscriptSegmentKey(s), s));
    }
    const ordered = [...byKey.values()].sort(compareLiveTranscriptSegments);
    return ordered.length > MAX_RENDERED_SEGMENTS
      ? ordered.slice(-MAX_RENDERED_SEGMENTS)
      : ordered;
  }, [fetched, liveState]);

  const partials = useMemo(() => {
    if (!live || !liveState) return [];
    return Object.values(liveState.partials).filter((s): s is ILiveTranscriptSegment => Boolean(s));
  }, [live, liveState]);

  const groups = useMemo(
    () => buildGroups([...committed, ...partials].sort(compareLiveTranscriptSegments)),
    [committed, partials],
  );

  const transcriptBaseMs = useMemo(() => {
    const starts = groups
      .map(g => g.startMs)
      .filter((ms): ms is number => typeof ms === 'number');
    return starts.length > 0 ? Math.min(...starts) : 0;
  }, [groups]);

  useEffect(() => {
    if (typeof focusStartMs !== 'number') return undefined;
    const match = groups.find(g => g.startMs === focusStartMs);
    if (!match) return undefined;
    const el = scrollRef.current?.querySelector<HTMLElement>(
      `[data-group-key="${match.key}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedKey(match.key);
    const timer = window.setTimeout(() => {
      setHighlightedKey(null);
    }, 2000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [focusStartMs, groups]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!live) {
      if (!startedAtTopRef.current && groups.length > 0) {
        el.scrollTop = 0;
        startedAtTopRef.current = true;
        pinnedRef.current = false;
        setPinned(false);
      }
      return;
    }
    if (pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [groups, live]);

  const isRenamable = (p: ICallScribeParticipant | undefined): boolean =>
    p !== undefined &&
    (p.nameSource === 0 ||
      p.nameSource === 'None' ||
      p.nameSource === 3 ||
      p.nameSource === 'CallerName' ||
      p.nameSource === 4 ||
      p.nameSource === 'Manual');

  const onRenamed = (participantId: number, groupKey: string, renamed: IRenamedSpeaker): void => {
    if (typeof callId === 'number') {
      dispatch(
        patchParticipant({
          callId,
          participantId,
          label: renamed.label,
          nameSource: renamed.nameSource,
          at: Date.now(),
          fallback: participants,
        }),
      );
    }
    window.setTimeout(() => {
      scrollRef.current
        ?.querySelector<HTMLElement>(`[data-group-key="${groupKey}"]`)
        ?.focus();
    }, 0);
  };

  const slots = useMemo(() => speakerSlots(participants), [participants]);
  const toneColour = (slot: number): string => {
    const tone = speakerTone(theme, slot);
    return onGround ? tone.stageText : tone.paperSolid;
  };

  const hasContent = groups.length > 0;

  useEffect(() => {
    if (!live || !listening || hasContent || typeof callId !== 'number') {
      setWaitingLong(false);
      refetchedRef.current = false;
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setWaitingLong(true);
      if (!refetchedRef.current) {
        refetchedRef.current = true;
        void getCallScribeTranscriptSegments(callId)
          .then(res => {
            if (res && Array.isArray(res.data) && res.data.length > 0) {
              setFetched(
                res.data.map(toLiveSegment),
              );
            }
          })
          .catch(() => undefined);
      }
    }, 20000);
    return () => { window.clearTimeout(timer); };
  }, [live, listening, hasContent, callId]);

  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    pinnedRef.current = atBottom;
    setPinned(atBottom);
  };

  const jumpToLatest = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    pinnedRef.current = true;
    setPinned(true);
  };

  if (typeof callId !== 'number') {
    return null;
  }

  const stageLive = variant === 'stage' && live;

  if (groups.length === 0 && !stageLive) {
    if (live) {
      return null;
    }
    if (fetchFailed) {
      return (
        <div className={classes.panel} role="alert">
          <div className={classes.label}>Conversation</div>
          <div className={classes.emptyText}>
            The transcript could not be loaded for this call.
          </div>
        </div>
      );
    }
    if (fetched === undefined) {
      return (
        <div className={classes.panel}>
          <div className={classes.label}>Conversation</div>
          <div className={classes.emptyText}>Loading the transcript…</div>
        </div>
      );
    }
    if (consentResult === null || consentResult === undefined) {
      return (
        <div className={classes.panel}>
          <div className={classes.label}>Conversation</div>
          <div className={classes.emptyText}>
            No transcript yet. It appears once the recording has been processed.
          </div>
        </div>
      );
    }
    return (
      <div className={classes.panel}>
        <div className={classes.label}>Conversation</div>
        <div className={classes.emptyText}>No live transcript for this call</div>
      </div>
    );
  }

  const lines = groups.map(g => {
    const slot = slotForLine(g, participants, slots);
    const interim = !g.committedText;
    const toneStyle: React.CSSProperties & { '--cs-tone': string } = { '--cs-tone': toneColour(slot) };
    return (
      <div
        key={g.key}
        data-group-key={g.key}
        data-tone={slot}
        data-partial={interim ? '' : undefined}
        aria-hidden={live && interim ? true : undefined}
        tabIndex={-1}
        className={[
          classes.line,
          interim ? classes.lineInterim : '',
          g.key === highlightedKey ? classes.groupHighlighted : '',
        ].filter(Boolean).join(' ')}
        style={toneStyle}
      >
        <span className={classes.rail} aria-hidden />
        <div className={classes.lineBody}>
          <div className={classes.lineMeta}>
            <span id={`speaker-${g.key}`} className={classes.lineName}>
              {participantLabel(g, participants)}
            </span>
            {typeof g.startMs === 'number' ? (
              <span className={classes.lineTime}>
                {formatMs(Math.max(0, g.startMs - transcriptBaseMs))}
              </span>
            ) : null}
            {canRename &&
              typeof g.participantId === 'number' &&
              g.participantId > 0 &&
              isRenamable(findParticipant(g, participants)) ? (
              <RenameSpeaker
                callId={callId}
                participantId={g.participantId}
                describedById={`speaker-${g.key}`}
                onRenamed={renamed => { onRenamed(g.participantId as number, g.key, renamed); }}
              />
            ) : null}
          </div>
          <p className={classes.lineText}>
            {g.committedText}
            {g.partialText ? (
              <span className={classes.partialTail} aria-hidden={live ? true : undefined}>
                {g.committedText ? ' ' : ''}
                {g.partialText}
              </span>
            ) : null}
            {live && g.partialText ? <span className={classes.liveCaret} aria-hidden /> : null}
          </p>
        </div>
      </div>
    );
  });

  const jump = !pinned && groups.length > 0 ? (
    <button type="button" className={classes.jumpPill} onClick={jumpToLatest}>
      <ArrowDownwardRoundedIcon aria-hidden fontSize="inherit" />
      {live ? 'Jump to live' : 'Jump to latest'}
    </button>
  ) : null;

  if (stageLive) {
    const empty = listening ? (
      <div className={classes.emptyState}>
        <span className={classes.emptyTitle}>
          {waitingLong ? 'Waiting for transcript…' : 'Listening for the first words'}
        </span>
        <span>Words appear here live, with each person labelled.</span>
      </div>
    ) : (
      <div className={classes.emptyState}>
        <span className={classes.emptyTitle}>Transcript starts when they answer</span>
        <span>Words appear here live, with each person labelled.</span>
      </div>
    );
    return (
      <section className={classes.panelStage} aria-labelledby={headingId}>
        <div className={classes.stageHeader}>
          <h2 id={headingId} className={classes.stageTitle}>Live transcript</h2>
          {listening ? (
            <span className={classes.liveStatus}>
              <span className={classes.liveDot} aria-hidden />
              Listening
            </span>
          ) : null}
        </div>
        <div
          ref={scrollRef}
          onScroll={onScroll}
          role="log"
          aria-live="polite"
          aria-atomic="false"
          aria-labelledby={headingId}
          tabIndex={0}
          data-transcript-scroll
          className={`${classes.scroll} ${classes.scrollStage}`}
        >
          {groups.length === 0 ? empty : lines}
        </div>
        {jump}
      </section>
    );
  }

  return (
    <div className={classes.panel}>
      <div className={classes.label}>Conversation</div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        data-transcript-scroll
        {...(live ? { role: 'log', 'aria-live': 'polite' as const, 'aria-atomic': false } : {})}
        className={classes.scroll}
      >
        {lines}
      </div>
      {jump}
    </div>
  );
};

export default ConversationSection;
